<script setup>
// One membership or pass the signed-in person holds, as a row of Profile →
// Memberships & passes (AppMyMemberships, the "Yours" list): the kind's icon
// (a pass is green), the type's name with its Pass chip and the "Answer the
// club's questions" chip, the club · group · renews line, and the status chip.
// Pulled out of ProfileMembershipsPanel so /features/memberships can run the
// real row on demo data.
import {computed} from 'vue';
import {membershipStatusChip, DISPLAY_AS} from '~/utils/memberships';
import {membershipRowLine} from '~/utils/membershipsMember';

const props = defineProps({
  /** A membership of the member list (`mine`): membershipTypeName, displayAs, clubName, groupLine, renewsOrEndsLine, status… */
  membership: {type: Object, required: true},
});
const emit = defineEmits(['open']);

const isPass = computed(() => props.membership.displayAs === DISPLAY_AS.pass);
const chip = computed(() => membershipStatusChip(props.membership));
</script>

<template>
  <button type="button" class="mrw" data-testid="membership-row" @click="emit('open', membership)">
    <div class="mrw-ic" :class="{'mrw-ic--pass': isPass}">
      <v-icon size="18" :icon="isPass ? 'mdi-ticket-confirmation-outline' : 'mdi-card-account-details-outline'"/>
    </div>
    <div class="flex-fill">
      <div class="mrw-title">
        {{ membership.membershipTypeName }}<span v-if="isPass" class="tl-chip tl-chip--primary mrw-chip">Pass</span>
        <span v-if="membership.needsOwnAnswers" class="tl-chip tl-chip--warn mrw-chip">Answer the club's questions</span>
      </div>
      <div class="mrw-sub">{{ membershipRowLine(membership) }}</div>
    </div>
    <span class="tl-chip" :class="chip.cls">{{ chip.label }}</span>
    <v-icon size="18" icon="mdi-chevron-right" class="mrw-chev"/>
  </button>
</template>

<style scoped lang="scss">
.mrw {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; width: 100%;
  border: 0; border-top: 1px solid rgb(var(--v-theme-borderLight)); background: none; text-align: left; color: inherit;
  font: inherit; cursor: pointer;
  &:first-child { border-top: 0; }
  &:hover { background: rgb(var(--v-theme-light)); }
}
.mrw-ic {
  width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgb(var(--v-theme-brandTint)); color: rgb(var(--v-theme-primary));
  &--pass { background: rgb(var(--v-theme-successTint)); color: rgb(var(--v-theme-success)); }
}
.mrw-title { font-size: 14px; font-weight: 700; letter-spacing: -0.2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.mrw-chip { font-size: 10.5px; }
.mrw-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; line-height: 1.4; }
.mrw-chev { color: rgb(var(--v-theme-textSubtle)); }
</style>
